import { useEffect, useRef, useState } from "react";
import { Stax, type StaxMood } from "@/components/stax/Stax";

interface Line {
  setup: string;
  punch?: string;
}

const GREETING: Line = { setup: "Hi, I’m Stax. I’ll look after your site.", punch: "Psst. Poke me." };

export const JOKES: Line[] = [
  { setup: "I’m reading a book about glue.", punch: "I just can’t put it down." },
  { setup: "Why don’t sticky notes ever get lost?", punch: "They always stick around." },
  { setup: "What’s a website’s favourite snack?", punch: "Cookies. Obviously." },
  { setup: "Why was the web page cold?", punch: "It left its Windows open." },
  { setup: "Why did the developer go broke?", punch: "They used up all their cache." },
  { setup: "I told my website a joke about margins.", punch: "It needed a bit more space." },
  { setup: "What do you call a sticky note that sings?", punch: "A Post-it Malone." },
];

const TYPE_MS = 28;
const PUNCH_PAUSE_MS = 900;
const SURPRISE_MS = 1600;

function prefersReducedMotion() {
  return typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
}

/** Types a line out: the setup, a beat, then the punchline. Shows it whole when motion is reduced. */
function useTypedLine(line: Line) {
  const [typed, setTyped] = useState({ setup: "", punch: "" });

  useEffect(() => {
    if (prefersReducedMotion()) {
      setTyped({ setup: line.setup, punch: line.punch ?? "" });
      return;
    }
    const timers: ReturnType<typeof setTimeout>[] = [];
    setTyped({ setup: "", punch: "" });
    let at = 0;
    for (let i = 1; i <= line.setup.length; i++) {
      at += TYPE_MS;
      timers.push(setTimeout(() => setTyped({ setup: line.setup.slice(0, i), punch: "" }), at));
    }
    if (line.punch) {
      const punch = line.punch;
      at += PUNCH_PAUSE_MS;
      for (let i = 1; i <= punch.length; i++) {
        at += TYPE_MS;
        timers.push(setTimeout(() => setTyped({ setup: line.setup, punch: punch.slice(0, i) }), at));
      }
    }
    return () => timers.forEach(clearTimeout);
  }, [line]);

  return typed;
}

interface StaxGreetingProps {
  /** `column` puts the bubble above Stax; `row` puts it beside. */
  layout: "column" | "row";
  size: number;
}

/** Stax on the sign-in screens. Poke it and it tells a (terrible) joke. */
export function StaxGreeting({ layout, size }: StaxGreetingProps) {
  const [told, setTold] = useState(-1);
  const [mood, setMood] = useState<StaxMood>("idle");
  const settle = useRef<ReturnType<typeof setTimeout>>(undefined);
  const line = told < 0 ? GREETING : JOKES[told % JOKES.length]!;
  const typed = useTypedLine(line);

  useEffect(() => () => clearTimeout(settle.current), []);

  const poke = () => {
    setTold((count) => count + 1);
    setMood("attention");
    clearTimeout(settle.current);
    settle.current = setTimeout(() => setMood("idle"), SURPRISE_MS);
  };

  const bubble = (
    <div
      className={[
        "relative rounded-2xl rounded-bl-sm bg-white px-4 py-2.5 text-sm text-zinc-900 shadow-sm ring-1 ring-zinc-200",
        layout === "column" ? "self-start max-w-xs" : "mb-10",
      ].join(" ")}
    >
      {/* The whole line holds the bubble's size while it types, so nothing jumps. */}
      <div className="grid" aria-hidden="true">
        <p className="invisible col-start-1 row-start-1">
          <span className="font-medium">{line.setup}</span>
          {line.punch && <span className="block mt-1">{line.punch}</span>}
        </p>
        <p className="col-start-1 row-start-1">
          <span className="font-medium">{typed.setup}</span>
          {typed.punch && <span className="block mt-1 text-orange-800">{typed.punch}</span>}
        </p>
      </div>
      <p className="sr-only" aria-live="polite">
        {line.setup} {line.punch}
      </p>
    </div>
  );

  const button = (
    <button
      type="button"
      onClick={poke}
      aria-label="Poke Stax for a joke"
      title="Poke me"
      className={[
        "rounded-3xl cursor-pointer transition-transform active:scale-95 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-orange-600",
        layout === "column" ? "self-center" : "-ml-3 shrink-0",
      ].join(" ")}
    >
      <Stax size={size} mood={mood} />
    </button>
  );

  return layout === "column" ? (
    <div className="flex flex-col gap-3">
      {bubble}
      {button}
    </div>
  ) : (
    <div className="flex min-h-[132px] items-end gap-2">
      {button}
      {bubble}
    </div>
  );
}
